import { InformationCircleIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "src/async/apiUtils";
import Preloader from "src/components/shared/LoadingAnimations/Preloader";
import { WorkspaceContext } from "src/context/workspace";
import { getNavigateLink } from "src/helpers/userHelper";
import BlurImage from "src/assets/images/blur_image.png";
import { classNames } from "src/helpers/classNames";
import { useWorkspaceSwitching } from "src/context/WorkspaceSwitchingContext";
import { useSocket } from "src/context/SocketContext";

const PermissionDenied = () => {
  const { workspace_id } = useParams();

  const location = useLocation();
  const navigate = useNavigate();
  const socket = useSocket();

  const [navigating, setNavigating] = useState(false);
  const { setWorspacePaymentModalOpen } = useContext(WorkspaceContext);
  const { finishSwitching } = useWorkspaceSwitching();

  const payment_required = window.location.href.includes("/payment-required");
  const permission_denied = window.location.href.includes("/permission-denied");

  useEffect(() => {
    const isRedirectedFromRouter = location.state?.from === "react-router-dom";
    const isAnchorTagPress = !location.state;
    const navigationEntries = performance.getEntriesByType("navigation");
    const isReloaded = navigationEntries.length > 0 && navigationEntries[0].type === "reload";

    if (isRedirectedFromRouter) {
    } else if (isReloaded) {
    } else if (isAnchorTagPress) {
    }
  }, [location]);

  useEffect(() => {
    handleRefresh("initial");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (permission_denied) {
      setTimeout(() => {
        finishSwitching();
      }, 300);
    }
  }, [finishSwitching, permission_denied]);

  useEffect(() => {
    if (payment_required) {
      setWorspacePaymentModalOpen(true);
    } else {
      setWorspacePaymentModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment_required]);

  const handleRefresh = async (from = "") => {
    try {
      // if (loading) return;
      if (from !== "initial") setNavigating(true);
      const { data: resData } = await apiRequest("GET", workspace_id ? `/portal/users/${workspace_id}/page` : `/portal/users/workspaces/page`, {}, { returnErrorWithstatus: true });
      if (resData.status === 200) {
        if (!resData?.is_content) {
          setTimeout(() => {
            finishSwitching();
          }, 300);
        }
        const navigate_link = getNavigateLink(resData);
        socket?.emit("join-workspace-request", { workspace_id: resData?.workspace_id });
        navigate(navigate_link);
      } else if (["WORKSPACE_PAUSED", "WORKSPACE_PAUSED_SCHEDULED"].includes(resData?.code)) {
        navigate(`/workspace/${workspace_id}/workspace-paused`, {
          state: {
            message: resData?.message,
            pause_expires_at: resData?.pause_expires_at,
          },
        });
      } else {
        navigate("/permission-denied");
      }
      setNavigating(false);
    } catch (error) {
      setTimeout(() => {
        finishSwitching();
      }, 300);
      if (error?.response?.data?.status === 402) {
        navigate(`/workspace/${workspace_id}/payment-required`);
      } else if (error?.response?.data?.status === 403 && ["WORKSPACE_PAUSED", "WORKSPACE_PAUSED_SCHEDULED"].includes(error?.response?.data?.code)) {
        navigate(`/workspace/${workspace_id}/workspace-paused`, {
          state: {
            message: error?.response?.data?.message,
            pause_expires_at: error?.response?.data?.pause_expires_at,
          },
        });
      } else {
        navigate("/permission-denied");
        setNavigating(false);
      }
    } finally {
    }
  };

  return (
    <div className={classNames("relative flex h-full w-full items-center justify-center overflow-hidden", payment_required ? "" : "px-6 py-12")}>
      {payment_required ? (
        <img
          src={BlurImage}
          alt="blur_img"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="relative  w-full max-w-xl  p-10">
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-100">
                <LockClosedIcon className="h-10 w-10 text-gray-500" />
              </div>
            </div>
          </div>

          <div className="space-y-6 text-center">
            <h1 className="text-3xl font-bold leading-tight text-gray-500">
              <span className="text-gray-500">{workspace_id ? "Access Pending" : "Access Restricted"}</span>
            </h1>

            <p className="mx-auto max-w-lg text-lg leading-relaxed text-gray-600">{workspace_id ? "Currently no pages are assigned to you. Content will be available once it has been assigned." : "You don't have the required permissions to access this content."}</p>

            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <InformationCircleIcon className="h-4 w-4" />
              <span>Contact your administrator for more details</span>
            </div>
          </div>

          <div className="mt-10 flex justify-center">
            <button
              onClick={handleRefresh}
              disabled={navigating}
              className={`group relative inline-flex transform items-center rounded-xl px-8 py-4 text-base font-semibold transition-all duration-300  focus:outline-none focus:ring-2 focus:ring-gray-500/50 focus:ring-offset-2 focus:ring-offset-transparent ${
                navigating ? "cursor-not-allowed bg-gray-300/50 text-gray-500" : "bg-gray-300/50 text-gray-500"
              }`}>
              <div className="relative flex items-center">
                {navigating && (
                  <div className="mr-2">
                    <Preloader
                      circleDimension="4"
                      text=""
                    />
                  </div>
                )}
                {navigating ? "Verifying Access..." : "Check Again"}
              </div>
            </button>
          </div>

          <div className="mt-8 flex justify-center">
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <div className={`h-2 w-2 rounded-full ${navigating ? "animate-pulse bg-gray-400" : "bg-gray-500"}`}></div>
              <span>{navigating ? "Checking permissions..." : "Access denied"}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionDenied;
